import { EMPTY, formatDate, formatDateFull, formatDecimal, formatDuration, formatInteger, formatTime, isNumber, toInstant } from './piFormat.js';
import { NO_CROSS_TOKEN_CODE, classifyError } from './piEnvelope.js';
import { PI_BASE_PATH, metricPathSegment, safeInternalRoute } from './piNavigation.js';
import { PERIOD_PRESETS, PI_FILTER_DEFAULTS, parseIsoDate, resolvePeriod } from './piQuery.js';
import { SALE_PRESETS, resolveSalePeriod } from './piOnboardingFilters.js';
import { COMPANY_ADOPTION_QUERY_KEYS } from './piFeatureCompaniesModel.js';
import { defineColumns } from './piColumns.js';
import { isPiPath } from './piAvailability.js';
import { UNLOCK_PATH } from './piUnlock.js';

export const ASSISTANT_PATH = PI_BASE_PATH + '/chat';

export const ASSISTANT_USAGE_PATH = PI_BASE_PATH + '/chat/consumo';

export const ASSISTANT_SUBVIEWS = [
  { key: 'chat', path: ASSISTANT_PATH, label: 'Conversa' },
  { key: 'usage', path: ASSISTANT_USAGE_PATH, label: 'Consumo da IA' },
];

export const ASSISTANT_LIMITS = {
  question: 1000,
  comment: 500,
  blocks: 6,
  rows: 15,
  columns: 8,
  items: 12,
  metrics: 12,
  followUps: 3,
  links: 6,
  turnsShown: 20,
  text: 2000,
};

export const DEFAULT_AGENT = 'auto';

export const AREA_AGENT_KEYS = ['journey', 'people_development', 'communication', 'culture', 'hr_documents', 'recruitment'];

export const AGENT_ORDER = [
  'auto', 'general', 'engagement', 'features', 'companies', 'activation', 'retention', 'operations', 'revenue', 'advanced_report', 'catalog',
].concat(AREA_AGENT_KEYS);

export const AGENT_LABELS = {
  auto: 'Automático',
  general: 'Genérico (todas as ferramentas)',
  engagement: 'Engajamento',
  features: 'Funcionalidades e adoção',
  companies: 'Empresas e contas',
  activation: 'Ativação e onboarding',
  retention: 'Retenção e coortes',
  operations: 'Operações e qualidade',
  revenue: 'Receita e oportunidades',
  advanced_report: 'Relatório avançado',
  catalog: 'Catálogo e definições',
  journey: 'Ponto e Jornada',
  people_development: 'Desenvolvimento de Pessoas',
  communication: 'Comunicação Interna',
  culture: 'Endomarketing, Clima e Benefícios',
  hr_documents: 'DP, Documentos e Assinaturas',
  recruitment: 'Recrutamento e Seleção',
};

export const AGENT_GROUPS = [
  { key: 'general', label: 'Geral' },
  { key: 'usage', label: 'Uso do produto (PI)' },
  { key: 'area', label: 'Áreas do produto' },
];

export const AREA_SUGGESTIONS_PER_AGENT = 2;

export const CHAT_PREFILL_QUERY = { agent: 'agent', question: 'q' };

export const CHAT_STORAGE_KEY = 'pi-chat:v1';

export const CHAT_QUERY = { conversation: 'chat', turn: 'turn' };

export const CHAT_BACK_SUMMARY = 60;

export const CHAT_FLASH_MS = 2000;

export const CHAT_VISITED_MAX = 200;

export const CHAT_SCREEN = 'Chat';

export const AREA_QUERY_KEYS = ['area', 'journey'];

export const STATUS_INFO = {
  answered: { label: 'Respondida', tone: 'ok' },
  no_data: { label: 'Sem dado no PI', tone: 'muted' },
  out_of_scope: { label: 'Fora do escopo', tone: 'muted' },
  needs_clarification: { label: 'Precisa de esclarecimento', tone: 'info' },
  limit_reached: { label: 'Limite da pergunta atingido', tone: 'warn' },
  error: { label: 'Erro', tone: 'bad' },
};

export const ROUTE_TEXT = {
  user: 'escolhido por você',
  rule: 'escolhido automaticamente',
  llm: 'escolhido automaticamente pelo roteador',
  fallback: 'sem especialista claro, foi para o Genérico',
};

export const TOOL_STATUS_INFO = {
  ok: { label: 'ok', tone: 'ok' },
  empty: { label: 'sem linhas', tone: 'muted' },
  invalid_args: { label: 'argumento inválido', tone: 'warn' },
  denied: { label: 'negada', tone: 'bad' },
  not_found: { label: 'não encontrado', tone: 'muted' },
  timeout: { label: 'tempo esgotado', tone: 'bad' },
  error: { label: 'erro', tone: 'bad' },
  skipped: { label: 'pulada', tone: 'muted' },
};

export const FEEDBACK_INFO = {
  useful: { label: 'Útil', tone: 'ok' },
  not_useful: { label: 'Não útil', tone: 'bad' },
};

export const SCREEN_LABELS = {
  overview: 'Visão geral',
  engagement: 'Engajamento',
  features: 'Funcionalidades',
  feature: 'Ficha da funcionalidade',
  activation: 'Ativação',
  retention: 'Retenção',
  operations: 'Operações',
  companies: 'Empresas',
  company: 'Ficha da empresa',
  opportunities: 'Oportunidades',
  status: 'Status e catálogo',
  areas: 'Áreas',
};

export const WARNING_TEXTS = {
  price_model_mismatch: 'O modelo devolvido pela Azure não é o precificado (gpt-4.1-mini): o custo foi calculado com o preço da gpt-4.1-mini.',
};

export const NOT_CONFIGURED_TEXT =
  'O assistente ainda não foi configurado neste ambiente: falta a chave da Azure no servidor (ProductAnalytics__Assistant__ApiKey).';

export const DISABLED_TEXT = 'O assistente está desligado neste ambiente ou o Product Intelligence ainda não foi publicado (404).';

export const PENDING_TEXT = 'Consultando o PI… pode levar até 90 s.';

export const NEW_CHAT_FOR_PREFILL_TEXT = 'Nova conversa com o especialista; a anterior continua no Consumo da IA.';

export const NUMBER_WARNING_HINT = 'Número que o assistente não achou nas consultas: confira na tela antes de usar.';

export const TYPICAL_QUESTION = { inputTokens: 18702, cachedInputTokens: 10112, outputTokens: 784 };

export const ASSISTANT_GUIDE = [
  'Pergunte em português sobre o que o PI mede: a resposta traz o número, o período, a definição usada e o caminho até a tela. Só leitura.',
  'Uma pergunta por vez; diga a funcionalidade, a empresa ou o período quando importar (ex.: "adoção do OKR em setembro entre gestores"). Sem período, vale o das telas: 30 dias fechados; na ativação, 6 meses de vendas.',
  'Deixe em Automático; escolha um especialista quando souber a área e o Genérico para perguntas que cruzam áreas.',
  'Leia a manchete, a tabela e "Como cheguei nisso" (consultas e filtros); confira no link da tela. Aviso amarelo = número que o assistente não achou nas consultas.',
  'Ele não responde sobre dados de pessoas, salário, folha, previsões nem faz ações (marcar, liberar, contatar).',
  'Avalie com "Útil" ou "Não útil": um clique registra; o comentário é opcional e ajuda a melhorar as respostas.',
];

export const HISTORY_PREFIX = 'pi:assistant:';

const MASTER_USER_KEY = 'mamUserMasterInfo';

const GUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const COMPANY_ID = /^[0-9A-Fa-f-]{1,40}$/;
const SHARD = /^[0-9A-Za-z]{1,8}$/;
const FEATURE_TYPE = /^\d{1,5}$/;
const AGENT_KEY = /^[a-z][a-z_]{0,29}$/;
const LINK_PATH = /^\/product-intelligence(?:\/[A-Za-z0-9_~%-]+(?:\.[A-Za-z0-9_~%-]+)*)*$/;
const FEATURE_LINK_PATH = /^\/product-intelligence\/funcionalidades\/\d{1,5}$/;
const AREA_LINK_PATH = /^\/product-intelligence\/areas(?:\/metricas\/[A-Za-z0-9_]+(?:[.~][A-Za-z0-9_]+)+)?$/;
const AREA_METRIC_LINK_PATH = /^(\/product-intelligence\/areas\/metricas\/)([A-Za-z0-9_]+(?:[.~][A-Za-z0-9_]+)+)$/;
const LINK_ID = /^[^\s"'<>\\]{1,300}$/;
const TURN_ANCHOR = /^[ip]\d{1,18}$/;
const NUMBER_PT = /^[+\-−]?(?:\d{1,3}(?:\.\d{3})+|\d+)(?:,\d+)?$/;
const PERCENT_PT = /^[+\-−]?(?:\d{1,3}(?:\.\d{3})+|\d+)(?:,\d+)?\s?%$/;
const DATE_PT = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const BLANK_CELLS = ['', '—', '–', '-', 'n/d', 'N/D'];
const CONTROL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g;
const UNSAFE_QUERY = /[\u0000-\u001f<>"'`]/;
const NUMBER_WARNING = /^n[uú]mero n[aã]o encontrado/i;
const NUMERIC_CELL = /^[+\-−]?(?:R\$|US\$)?\s?\d[\d.,]*(?:\s?(?:%|p\.p\.|ms|s|min|h|dias?))?$/;
const TABLE_SEPARATOR = /^\s*\|?\s*:?-+:?\s*(?:\|\s*:?-+:?\s*)*\|?\s*$/;
const BULLET_LINE = /^\s*(?:[-*•]|\d{1,2}[.)])\s+(.+)$/;
const HEADING_LINE = /^\s*#{1,6}\s+(.+)$/;
const SCREEN_ROUTES = [
  { pattern: /^\/product-intelligence\/funcionalidades\/(\d{1,5})\/?$/, screen: 'feature', kind: 'feature' },
  { pattern: /^\/product-intelligence\/empresas\/([0-9A-Fa-f-]{1,40})\/([0-9A-Za-z]{1,8})\/?$/, screen: 'company', kind: 'company' },
  { pattern: /^\/product-intelligence\/?$/, screen: 'overview' },
  { pattern: /^\/product-intelligence\/engajamento\/?$/, screen: 'engagement' },
  { pattern: /^\/product-intelligence\/funcionalidades\/?$/, screen: 'features' },
  { pattern: /^\/product-intelligence\/ativacao\/?$/, screen: 'activation' },
  { pattern: /^\/product-intelligence\/retencao\/?$/, screen: 'retention' },
  { pattern: /^\/product-intelligence\/operacoes\/?$/, screen: 'operations' },
  { pattern: /^\/product-intelligence\/empresas\/?$/, screen: 'companies' },
  { pattern: /^\/product-intelligence\/oportunidades\/?$/, screen: 'opportunities' },
  { pattern: /^\/product-intelligence\/status\/?$/, screen: 'status' },
  { pattern: /^\/product-intelligence\/areas(?:\/metricas\/[A-Za-z0-9_]+(?:[.~][A-Za-z0-9_]+)+)?\/?$/, screen: 'areas' },
];

const BLOCK_TYPES = ['paragraph', 'bullets', 'table', 'metrics'];

const has = (source, key) => Object.prototype.hasOwnProperty.call(source, key);

export const objectOf = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});

const listOf = (value) => (Array.isArray(value) ? value : []);

const numberOr = (value, fallback) => (isNumber(value) ? value : fallback);

const intOr = (value, fallback) => (isNumber(value) ? Math.round(value) : fallback);

export const cleanText = (value, max) => {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') return '';
  const clean = String(value).replace(/\r\n?/g, '\n').replace(/\t/g, ' ').replace(CONTROL, '').trim();
  const limit = isNumber(max) ? max : ASSISTANT_LIMITS.text;
  return clean.length > limit ? clean.substring(0, limit - 1) + '…' : clean;
};

const stringList = (value, max, size) => listOf(value)
  .map((item) => cleanText(item, size))
  .filter(Boolean)
  .slice(0, max);

export const isGuid = (value) => typeof value === 'string' && GUID.test(value);

const joinPt = (items) => {
  if (items.length <= 1) return items.join('');
  return items.slice(0, -1).join(', ') + ' e ' + items[items.length - 1];
};

export const agentLabel = (key, fallback) => {
  const label = cleanText(fallback, 80);
  if (label) return label;
  return has(AGENT_LABELS, key) ? AGENT_LABELS[key] : (cleanText(key, 40) || EMPTY);
};

export const statusInfo = (status) => (has(STATUS_INFO, status) ? STATUS_INFO[status] : { label: cleanText(status, 40) || EMPTY, tone: 'muted' });

export const toolStatusInfo = (status) => (has(TOOL_STATUS_INFO, status) ? TOOL_STATUS_INFO[status] : { label: cleanText(status, 40) || EMPTY, tone: 'muted' });

export const feedbackInfo = (value) => (has(FEEDBACK_INFO, value) ? FEEDBACK_INFO[value] : null);

export const formatMoney = (value, currency) => {
  if (!isNumber(value)) return EMPTY;
  const prefix = currency === 'BRL' ? 'R$ ' : 'US$ ';
  const size = Math.abs(value);
  let digits = 2;
  if (size > 0 && size < 1) digits = Math.min(6, Math.max(2, 1 - Math.floor(Math.log10(size))));
  return prefix + formatDecimal(value, digits);
};

export const formatMoneyExact = (value, currency) => {
  if (!isNumber(value)) return EMPTY;
  return (currency === 'BRL' ? 'R$ ' : 'US$ ') + formatDecimal(value, 6);
};

export const costPair = (usd, brl) => {
  if (!isNumber(usd)) return EMPTY;
  return formatMoney(usd, 'USD') + (isNumber(brl) ? ' (' + formatMoney(brl, 'BRL') + ')' : '');
};

export const stampText = (value) => {
  const instant = toInstant(value);
  return instant ? formatDateFull(instant) + ' ' + formatTime(instant) : EMPTY;
};

export const latencyText = (value) => (isNumber(value) ? formatDuration(value) : EMPTY);

const roundMicro = (value) => Math.round(value * 1000000) / 1000000;

export const estimateCost = (pricing, tokens) => {
  const price = objectOf(pricing);
  const usage = objectOf(tokens || TYPICAL_QUESTION);
  const input = numberOr(usage.inputTokens, 0);
  const cached = Math.min(input, numberOr(usage.cachedInputTokens, 0));
  const output = numberOr(usage.outputTokens, 0);
  if (!isNumber(price.inputPer1MUsd) || !isNumber(price.cachedInputPer1MUsd) || !isNumber(price.outputPer1MUsd)) return null;
  const usd = roundMicro(((input - cached) * price.inputPer1MUsd + cached * price.cachedInputPer1MUsd + output * price.outputPer1MUsd) / 1000000);
  const brl = isNumber(price.usdBrlRate) ? roundMicro(usd * price.usdBrlRate) : null;
  return { usd: usd, brl: brl };
};

export const defaultAgentGroup = (key) => {
  if (key === DEFAULT_AGENT || key === 'general') return 'general';
  return AREA_AGENT_KEYS.indexOf(key) !== -1 ? 'area' : 'usage';
};

const agentGroupOf = (value, key) => (AGENT_GROUPS.some((group) => group.key === value) ? value : defaultAgentGroup(key));

const normalizeAgent = (raw) => {
  const source = objectOf(raw);
  const key = typeof source.key === 'string' && AGENT_KEY.test(source.key) ? source.key : '';
  if (!key) return null;
  return {
    key: key,
    label: agentLabel(key, source.label),
    description: cleanText(source.description, 400),
    group: agentGroupOf(source.group, key),
    expertise: cleanText(source.expertise, 400),
    isDefault: source.isDefault === true,
    tools: stringList(source.tools, 40, 60),
    suggestions: stringList(source.suggestedQuestions, 8, 300),
  };
};

const orderOf = (key) => {
  const index = AGENT_ORDER.indexOf(key);
  return index === -1 ? AGENT_ORDER.length : index;
};

export const normalizeAgents = (raw) => {
  const source = objectOf(raw);
  const limits = objectOf(source.limits);
  const today = objectOf(source.usageToday);
  const pricing = objectOf(source.pricing);
  const seen = {};
  const agents = listOf(source.agents)
    .map(normalizeAgent)
    .filter((agent) => {
      if (!agent || seen[agent.key]) return false;
      seen[agent.key] = true;
      return true;
    })
    .map((agent, index) => ({ agent: agent, index: index }))
    .sort((a, b) => (orderOf(a.agent.key) - orderOf(b.agent.key)) || (a.index - b.index))
    .map((item) => item.agent);
  if (!seen[DEFAULT_AGENT]) {
    agents.unshift({
      key: DEFAULT_AGENT, label: AGENT_LABELS.auto, description: '', group: 'general', expertise: '', isDefault: true, tools: [], suggestions: [],
    });
  }
  const maxQuestion = intOr(limits.maxQuestionLength, ASSISTANT_LIMITS.question);
  return {
    configured: source.configured === true,
    enabled: source.enabled !== false,
    model: cleanText(source.model, 80),
    deployment: cleanText(source.deployment, 80),
    promptVersion: cleanText(source.promptVersion, 60),
    knowledgeVersion: cleanText(source.knowledgeVersion, 60),
    limits: {
      perUser10Min: intOr(limits.perUser10Min, null),
      perUserDay: intOr(limits.perUserDay, null),
      dailyCostCeilingUsd: numberOr(limits.dailyCostCeilingUsd, null),
      maxQuestionLength: maxQuestion > 0 && maxQuestion <= ASSISTANT_LIMITS.question ? maxQuestion : ASSISTANT_LIMITS.question,
      maxTurnsInContext: intOr(limits.maxTurnsInContext, null),
    },
    usageToday: {
      questions: intOr(today.questions, 0),
      userQuestions: intOr(today.userQuestions, 0),
      user10Min: intOr(today.user10Min, 0),
      costUsd: numberOr(today.costUsd, 0),
      costBrl: numberOr(today.costBrl, 0),
      ceilingReached: today.ceilingReached === true,
    },
    pricing: {
      priceVersion: cleanText(pricing.priceVersion, 80),
      inputPer1MUsd: numberOr(pricing.inputPer1MUsd, null),
      cachedInputPer1MUsd: numberOr(pricing.cachedInputPer1MUsd, null),
      outputPer1MUsd: numberOr(pricing.outputPer1MUsd, null),
      usdBrlRate: numberOr(pricing.usdBrlRate, null),
    },
    agents: agents,
  };
};

export const agentByKey = (agents, key) => listOf(agents).filter((agent) => agent && agent.key === key)[0] || null;

export const defaultAgentKey = (agents) => {
  const list = listOf(agents);
  const preferred = list.filter((agent) => agent && agent.isDefault)[0];
  if (preferred) return preferred.key;
  return agentByKey(list, DEFAULT_AGENT) ? DEFAULT_AGENT : (list[0] ? list[0].key : DEFAULT_AGENT);
};

export const agentGroups = (agents) => AGENT_GROUPS
  .map((group) => ({ key: group.key, label: group.label, agents: listOf(agents).filter((agent) => agent && agent.group === group.key) }))
  .filter((group) => group.agents.length > 0);

export const EXPERTISE_PREFIX = 'Especialista em ';

export const agentHelp = (agent) => {
  const source = objectOf(agent);
  const text = cleanText(source.expertise, 400);
  const expertise = text.indexOf(EXPERTISE_PREFIX) === 0 ? text.substring(EXPERTISE_PREFIX.length).trim() : text;
  const description = cleanText(source.description, 400);
  return { expertise: expertise, description: !expertise || source.group === 'area' ? description : '' };
};

export const areaSuggestions = (agents) => listOf(agents)
  .filter((agent) => agent && agent.group === 'area' && listOf(agent.suggestions).length > 0)
  .map((agent) => ({ key: agent.key, label: agent.label, questions: agent.suggestions.slice(0, AREA_SUGGESTIONS_PER_AGENT) }));

export const costNoticeText = (view) => {
  const source = objectOf(view);
  const limits = objectOf(source.limits);
  const estimate = estimateCost(source.pricing);
  const parts = [];
  if (estimate) parts.push('Estimativa: uma pergunta típica custa cerca de ' + costPair(estimate.usd, estimate.brl) + '; o custo real vem em cada resposta.');
  const caps = [];
  if (isNumber(limits.perUser10Min)) caps.push(limits.perUser10Min + ' perguntas a cada 10 min');
  if (isNumber(limits.perUserDay)) caps.push(limits.perUserDay + ' por dia');
  let line = caps.length ? 'Até ' + joinPt(caps) + ' por pessoa' : '';
  if (isNumber(limits.dailyCostCeilingUsd)) {
    line += (line ? '; teto de ' : 'Teto de ') + formatMoney(limits.dailyCostCeilingUsd, 'USD') + ' por dia para todos';
  }
  if (line) parts.push(line + '.');
  parts.push('As perguntas ficam registradas com seu nome e e-mail em Consumo da IA.');
  return parts.join(' ');
};

export const usageTodayText = (view) => {
  const source = objectOf(view);
  const limits = objectOf(source.limits);
  const today = objectOf(source.usageToday);
  const mine = numberOr(today.userQuestions, 0);
  const recent = numberOr(today.user10Min, 0);
  const parts = [];
  parts.push('Hoje: ' + formatInteger(mine) + (isNumber(limits.perUserDay) ? ' de ' + formatInteger(limits.perUserDay) : '') +
    (mine === 1 ? ' pergunta sua' : ' perguntas suas'));
  parts.push(formatInteger(recent) + (isNumber(limits.perUser10Min) ? ' de ' + formatInteger(limits.perUser10Min) : '') + ' nos últimos 10 min');
  parts.push('custo de todos hoje ' + formatMoney(numberOr(today.costUsd, 0), 'USD') +
    (isNumber(limits.dailyCostCeilingUsd) ? ' de ' + formatMoney(limits.dailyCostCeilingUsd, 'USD') : ''));
  return parts.join(' · ');
};

export const applyUsage = (view, answer) => {
  const source = objectOf(view);
  const today = objectOf(source.usageToday);
  const usage = objectOf(objectOf(answer).usage);
  const limits = objectOf(source.limits);
  const costUsd = roundMicro(numberOr(today.costUsd, 0) + numberOr(usage.costUsd, 0));
  const next = {
    questions: numberOr(today.questions, 0) + 1,
    userQuestions: numberOr(today.userQuestions, 0) + 1,
    user10Min: numberOr(today.user10Min, 0) + 1,
    costUsd: costUsd,
    costBrl: roundMicro(numberOr(today.costBrl, 0) + numberOr(usage.costBrl, 0)),
    ceilingReached: today.ceilingReached === true || (isNumber(limits.dailyCostCeilingUsd) && costUsd >= limits.dailyCostCeilingUsd),
  };
  return Object.assign({}, source, { usageToday: next });
};

export const cleanQuestion = (value) => {
  if (typeof value !== 'string') return '';
  return value.replace(/\r\n?/g, '\n').replace(/\t/g, ' ').replace(CONTROL, '').replace(/\n{3,}/g, '\n\n').trim();
};

export const questionError = (value, max) => {
  const limit = isNumber(max) && max > 0 ? max : ASSISTANT_LIMITS.question;
  const question = cleanQuestion(value);
  if (!question) return 'Escreva a pergunta.';
  if (question.length > limit) return 'A pergunta passa de ' + formatInteger(limit) + ' caracteres.';
  return '';
};

export const originContext = (path) => {
  const value = typeof path === 'string' ? path.split('?')[0].split('#')[0] : '';
  for (let i = 0; i < SCREEN_ROUTES.length; i++) {
    const route = SCREEN_ROUTES[i];
    const match = route.pattern.exec(value);
    if (!match) continue;
    const context = { screen: route.screen, featureType: null, companyId: null, shardCode: null };
    if (route.kind === 'feature') {
      const featureType = Number(match[1]);
      if (featureType > 32767) return null;
      context.featureType = featureType;
    }
    if (route.kind === 'company') {
      context.companyId = match[1];
      context.shardCode = match[2].toUpperCase();
    }
    return context;
  }
  return null;
};

const queryText = (query, key) => {
  const raw = query[key];
  const value = Array.isArray(raw) ? raw[0] : raw;
  return typeof value === 'string' ? value.trim() : (isNumber(value) ? String(value) : '');
};

const presetGiven = (presets, value) => presets.some((preset) => preset.value === value);

export const screenPeriod = (query, screen, now) => {
  const source = objectOf(query);
  const sale = screen === 'activation';
  const from = queryText(source, sale ? 'saleFrom' : 'from');
  const to = queryText(source, sale ? 'saleTo' : 'to');
  const preset = queryText(source, sale ? 'salePreset' : 'preset');
  const named = presetGiven(sale ? SALE_PRESETS : PERIOD_PRESETS, preset);
  if (!from && !to && !named) return null;
  const period = sale
    ? resolveSalePeriod({ saleFrom: from, saleTo: to, salePreset: preset }, now)
    : resolvePeriod({ preset: preset, from: from, to: to }, now);
  if (period.error && !named) return null;
  return { from: period.from, to: period.to };
};

export const contextFromRoute = (query, originPath, now) => {
  const source = objectOf(query);
  const origin = originContext(originPath) || {};
  const context = { from: null, to: null, featureType: null, companyId: null, shardCode: null, screen: origin.screen || null };
  const period = screenPeriod(source, context.screen, now);
  if (period) {
    context.from = period.from;
    context.to = period.to;
  }
  if (isNumber(origin.featureType)) context.featureType = origin.featureType;
  else {
    const featureType = queryText(source, 'featureType');
    if (FEATURE_TYPE.test(featureType) && Number(featureType) <= 32767) context.featureType = Number(featureType);
  }
  if (origin.companyId) {
    context.companyId = origin.companyId;
    context.shardCode = origin.shardCode;
  } else {
    const companyId = queryText(source, 'companyId');
    if (COMPANY_ID.test(companyId)) {
      context.companyId = companyId;
      const shard = queryText(source, 'shard');
      if (SHARD.test(shard)) context.shardCode = shard.toUpperCase();
    }
  }
  const filled = Object.keys(context).some((key) => context[key] !== null);
  return filled ? context : null;
};

export const contextCompanyName = (query, context) => {
  const source = objectOf(query);
  const target = objectOf(context);
  if (!target.companyId || queryText(source, 'companyId') !== target.companyId) return '';
  return cleanText(queryText(source, 'companyName'), 120);
};

const cutQuestion = (value, max) => {
  const question = cleanQuestion(value);
  const limit = isNumber(max) && max > 0 ? Math.min(max, ASSISTANT_LIMITS.question) : ASSISTANT_LIMITS.question;
  return question.length > limit ? question.substring(0, limit) : question;
};

export const hasChatPrefill = (query) => {
  const source = objectOf(query);
  return has(source, CHAT_PREFILL_QUERY.agent) || has(source, CHAT_PREFILL_QUERY.question);
};

export const chatPrefill = (query, agents, max) => {
  const source = objectOf(query);
  const key = queryText(source, CHAT_PREFILL_QUERY.agent);
  return {
    agent: AGENT_KEY.test(key) && agentByKey(agents, key) ? key : '',
    question: cutQuestion(queryText(source, CHAT_PREFILL_QUERY.question), max),
  };
};

export const prefillStartsNewChat = (state, prefill) => {
  const turns = listOf(objectOf(state).turns);
  const agent = objectOf(prefill).agent;
  if (!turns.length || typeof agent !== 'string' || !agent) return false;
  const last = objectOf(objectOf(turns[turns.length - 1]).answer);
  return cleanText(last.agentUsed, 40) !== agent && cleanText(last.agentRequested, 40) !== agent;
};

export const withoutChatPrefill = (query) => {
  const source = objectOf(query);
  const next = {};
  Object.keys(source).forEach((key) => {
    if (key !== CHAT_PREFILL_QUERY.agent && key !== CHAT_PREFILL_QUERY.question) next[key] = source[key];
  });
  return next;
};

export const chatPrefillRoute = (agent, question) => {
  const query = {};
  if (typeof agent === 'string' && AGENT_KEY.test(agent)) query[CHAT_PREFILL_QUERY.agent] = agent;
  const text = cutQuestion(question);
  if (text) query[CHAT_PREFILL_QUERY.question] = text;
  return { path: ASSISTANT_PATH, query: query };
};

export const companyNameFrom = (rows, companyId, shardCode) => {
  const id = typeof companyId === 'string' ? companyId.toLowerCase() : '';
  if (!id) return '';
  const shard = typeof shardCode === 'string' ? shardCode.toUpperCase() : '';
  const found = listOf(rows).map(objectOf).filter((row) => typeof row.companyId === 'string' && row.companyId.toLowerCase() === id &&
    (!shard || String(row.shardCode || '').toUpperCase() === shard))[0];
  const name = found ? cleanText(found.name, 120) : '';
  return name && name.toLowerCase() !== id ? name : '';
};

const SCREENS_IMPLIED = ['feature', 'company'];

export const contextLabel = (context, names) => {
  const source = objectOf(context);
  const labels = objectOf(names);
  const parts = [];
  if (source.screen && SCREENS_IMPLIED.indexOf(source.screen) === -1) {
    parts.push('tela ' + (has(SCREEN_LABELS, source.screen) ? SCREEN_LABELS[source.screen] : source.screen));
  }
  if (isNumber(source.featureType)) {
    const name = cleanText(labels.featureName, 80);
    parts.push(name ? name + ' (' + source.featureType + ')' : 'funcionalidade ' + source.featureType);
  }
  if (source.companyId) {
    const name = cleanText(labels.companyName, 120);
    parts.push(name || 'empresa ' + source.companyId.substring(0, 8) + (source.companyId.length > 8 ? '…' : '') +
      (source.shardCode ? ' (' + source.shardCode + ')' : ''));
  }
  if (source.from && source.to) parts.push((source.screen === 'activation' ? 'vendas de ' : '') + formatDate(source.from) + '–' + formatDate(source.to));
  if (!parts.length && source.screen) parts.push('tela ' + (has(SCREEN_LABELS, source.screen) ? SCREEN_LABELS[source.screen] : source.screen));
  return parts.length ? 'Contexto: ' + parts.join(', ') : '';
};

export const buildAskBody = (question, agent, conversationId, context) => {
  const source = context ? objectOf(context) : null;
  return {
    question: cleanQuestion(question),
    agent: typeof agent === 'string' && AGENT_KEY.test(agent) ? agent : DEFAULT_AGENT,
    conversationId: isGuid(conversationId) ? conversationId : null,
    context: source ? {
      from: source.from && source.to ? source.from : null,
      to: source.from && source.to ? source.to : null,
      featureType: isNumber(source.featureType) ? source.featureType : null,
      companyId: source.companyId || null,
      shardCode: source.companyId && source.shardCode ? source.shardCode : null,
      screen: source.screen || null,
    } : null,
  };
};

const segmentPattern = () => /\*\*([^*\n]+)\*\*|__([^_\n]+)__|`([^`\n]+)`|\[([^\]\n]+)\]\((?:[^()\n]|\([^()\n]*\))*\)/g;

export const inlineSegments = (value) => {
  const source = cleanText(value);
  const segments = [];
  const pattern = segmentPattern();
  let last = 0;
  let match = pattern.exec(source);
  while (match) {
    if (match.index > last) segments.push({ text: source.substring(last, match.index), strong: false, code: false });
    if (match[1] || match[2]) segments.push({ text: match[1] || match[2], strong: true, code: false });
    else if (match[3]) segments.push({ text: match[3], strong: false, code: true });
    else segments.push({ text: match[4], strong: false, code: false });
    last = match.index + match[0].length;
    match = pattern.exec(source);
  }
  if (last < source.length) segments.push({ text: source.substring(last), strong: false, code: false });
  return segments.map((segment, index) => Object.assign({ key: 's' + index }, segment));
};

const pipeCells = (line) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());

const isPipeLine = (line) => /^\s*\|.*\|\s*$/.test(line);

export const isNumericText = (value) => typeof value === 'string' && NUMERIC_CELL.test(value.trim());

export const plainText = (value) => inlineSegments(value).map((segment) => segment.text).join('');

const blankCell = (value) => BLANK_CELLS.indexOf(value === null || value === undefined ? '' : String(value).trim()) !== -1;

const isoFromPt = (value) => {
  const match = DATE_PT.exec(String(value).trim());
  if (!match) return '';
  const iso = match[3] + '-' + match[2] + '-' + match[1];
  return parseIsoDate(iso) ? iso : '';
};

export const parsePtNumber = (value) => {
  const text = value === null || value === undefined ? '' : String(value).trim().replace(/\s?%$/, '');
  if (!NUMBER_PT.test(text)) return null;
  const negative = /^[\-−]/.test(text);
  const number = Number(text.replace(/^[+\-−]/, '').replace(/\./g, '').replace(',', '.'));
  if (!isFinite(number)) return null;
  return negative && number !== 0 ? -number : number;
};

export const answerColumnType = (values) => {
  const filled = listOf(values).map((value) => (value === null || value === undefined ? '' : String(value).trim())).filter((value) => !blankCell(value));
  if (!filled.length) return 'text';
  if (filled.every((value) => PERCENT_PT.test(value))) return 'percent';
  if (filled.every((value) => !!isoFromPt(value))) return 'date';
  if (filled.every((value) => NUMBER_PT.test(value))) return filled.every((value) => value.indexOf(',') === -1) ? 'int' : 'decimal';
  return 'text';
};

export const answerCellValue = (value, type) => {
  if (blankCell(value)) return type === 'text' ? '' : null;
  if (type === 'int' || type === 'decimal' || type === 'percent') return parsePtNumber(value);
  if (type === 'date') return isoFromPt(value) || null;
  return String(value);
};

const sheetColumn = (index, label, type) => ({
  key: 'c' + index,
  label: label || 'Coluna ' + (index + 1),
  type: type,
  value: (row) => answerCellValue(listOf(row)[index], type),
});

export const answerTableSheet = (columns, rows) => {
  const headers = listOf(columns).map(plainText);
  const body = listOf(rows).map((row) => listOf(row).map(plainText));
  const width = body.reduce((max, row) => Math.max(max, row.length), headers.length);
  const specs = [];
  for (let index = 0; index < width; index++) {
    specs.push(sheetColumn(index, headers[index], answerColumnType(body.map((row) => row[index]))));
  }
  return { columns: defineColumns(specs), rows: body };
};

const tableCells = (cells, width) => {
  const list = cells.slice(0, width);
  while (list.length < width) list.push('');
  return list.map((cell, index) => ({ key: 'c' + index, text: cell, numeric: isNumericText(cell) }));
};

const tablePart = (columns, rows, linkOf) => {
  const width = Math.min(ASSISTANT_LIMITS.columns, Math.max(columns.length, rows.reduce((max, row) => Math.max(max, row.cells.length), 0)));
  const body = rows.slice(0, ASSISTANT_LIMITS.rows).map((row, index) => ({
    key: 'r' + index,
    cells: tableCells(row.cells, width).map((cell, position) => Object.assign(cell, {
      link: typeof linkOf === 'function' ? linkOf(row.index, position, cell.text) : null,
    })),
  }));
  const header = tableCells(columns, width).map((column, position) => {
    const filled = body.map((row) => row.cells[position]).filter((cell) => cell.text);
    return Object.assign({}, column, { numeric: filled.length > 0 && filled.every((cell) => cell.numeric) });
  });
  return {
    header: columns.some(Boolean),
    columns: header,
    rows: body,
    omitted: Math.max(0, rows.length - ASSISTANT_LIMITS.rows),
    sheet: answerTableSheet(columns, rows.map((row) => row.cells)),
  };
};

export const markdownBlocks = (value) => {
  const lines = cleanText(value).split('\n');
  const parts = [];
  let paragraph = [];
  let bullets = [];
  const flushParagraph = () => {
    if (paragraph.length) parts.push({ type: 'paragraph', text: paragraph.join('\n') });
    paragraph = [];
  };
  const flushBullets = () => {
    if (bullets.length) {
      parts.push({ type: 'bullets', items: bullets.slice(0, ASSISTANT_LIMITS.items).map((text, position) => ({ key: 'i' + position, text: text })) });
    }
    bullets = [];
  };
  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    if (isPipeLine(line) && index + 1 < lines.length && TABLE_SEPARATOR.test(lines[index + 1])) {
      flushParagraph();
      flushBullets();
      const columns = pipeCells(line);
      const rows = [];
      index += 2;
      while (index < lines.length && isPipeLine(lines[index])) {
        rows.push({ index: rows.length, cells: pipeCells(lines[index]) });
        index += 1;
      }
      parts.push(Object.assign({ type: 'table' }, tablePart(columns, rows, null)));
      continue;
    }
    const bullet = BULLET_LINE.exec(line);
    if (bullet) {
      flushParagraph();
      bullets.push(bullet[1].trim());
      index += 1;
      continue;
    }
    flushBullets();
    const heading = HEADING_LINE.exec(line);
    if (!line.trim()) flushParagraph();
    else paragraph.push(heading ? heading[1].trim() : line.trim());
    index += 1;
  }
  flushParagraph();
  flushBullets();
  return parts.map((part, position) => Object.assign({ key: 'p' + position }, part));
};

const cellText = (value) => cleanText(value, 300);

const paragraphBlock = (key, title, text) => {
  if (!text && !title) return null;
  return { key: key, type: 'paragraph', title: title, parts: text ? markdownBlocks(text) : [] };
};

const cellIndex = (value) => (isNumber(value) && value >= 0 && value < 1000 && Math.floor(value) === value ? value : null);

export const normalizeCellLinks = (list) => listOf(list)
  .map((raw) => {
    const source = objectOf(raw);
    const block = cellIndex(source.block);
    const row = cellIndex(source.row);
    const bullet = source.col === null || source.col === undefined;
    const col = bullet ? null : cellIndex(source.col);
    const companyId = typeof source.companyId === 'string' && COMPANY_ID.test(source.companyId) ? source.companyId : '';
    const shardCode = typeof source.shardCode === 'string' && SHARD.test(source.shardCode) ? source.shardCode.toUpperCase() : '';
    if (block === null || row === null || (!bullet && col === null) || !companyId || !shardCode) return null;
    return { block: block, row: row, col: col, companyId: companyId, shardCode: shardCode, query: safeQuery(source.query, []) };
  })
  .filter(Boolean);

const cellLinkAt = (links, block, row, col, text) => {
  const found = listOf(links).filter((link) => link.block === block && link.row === row && link.col === col)[0];
  if (!found) return null;
  return {
    id: 'cell:' + block + ':' + row + ':' + (col === null ? 'i' : col),
    companyId: found.companyId,
    shardCode: found.shardCode,
    query: found.query,
    name: plainText(text),
  };
};

const indexedItems = (value) => listOf(value)
  .map((item, index) => ({ index: index, text: cleanText(item, 600) }))
  .filter((item) => item.text)
  .slice(0, ASSISTANT_LIMITS.items);

export const normalizeBlock = (raw, index, links) => {
  const source = objectOf(raw);
  const position = isNumber(index) ? index : 0;
  const key = 'b' + position;
  const type = BLOCK_TYPES.indexOf(source.type) !== -1 ? source.type : 'paragraph';
  const title = cleanText(source.title, 200);
  const text = cleanText(source.text);
  const listed = indexedItems(source.items);
  const items = listed.map((item) => item.text);
  if (type === 'table') {
    const columns = listOf(source.columns).map(cellText);
    const rows = [];
    listOf(source.rows).forEach((row, rowIndex) => {
      if (Array.isArray(row)) rows.push({ index: rowIndex, cells: row.map(cellText) });
    });
    if (columns.length || rows.length) {
      const linkOf = (row, col, cell) => cellLinkAt(links, position, row, col, cell);
      return Object.assign({ key: key, type: 'table', title: title, note: text }, tablePart(columns, rows, linkOf));
    }
  }
  if (type === 'metrics') {
    const metrics = listOf(source.metrics)
      .map((metric, position) => {
        const item = objectOf(metric);
        return { key: 'm' + position, label: cleanText(item.label, 120), value: cleanText(item.value, 60), note: cleanText(item.note, 200) };
      })
      .filter((metric) => metric.label || metric.value)
      .slice(0, ASSISTANT_LIMITS.metrics);
    if (metrics.length) return { key: key, type: 'metrics', title: title, metrics: metrics, note: text };
  }
  if (type === 'bullets' && items.length) {
    return {
      key: key,
      type: 'bullets',
      title: title,
      items: listed.map((item, itemPosition) => ({ key: 'i' + itemPosition, text: item.text, link: cellLinkAt(links, position, item.index, null, item.text) })),
      note: text,
    };
  }
  return paragraphBlock(key, title, text || items.join('\n'));
};

export const blockParts = (block) => {
  const source = objectOf(block);
  if (source.type === 'paragraph') return listOf(source.parts);
  if (source.type === 'bullets') return [{ key: 'p0', type: 'bullets', items: listOf(source.items) }];
  if (source.type === 'table') {
    return [{
      key: 'p0', type: 'table', header: source.header === true, columns: listOf(source.columns), rows: listOf(source.rows), omitted: source.omitted || 0,
      sheet: source.sheet || answerTableSheet([], []),
    }];
  }
  if (source.type === 'metrics') return [{ key: 'p0', type: 'metrics', metrics: listOf(source.metrics) }];
  return [];
};

const titledTables = (blocks) => {
  let count = 0;
  return blocks.map((block) => {
    let first = true;
    const title = plainText(block.title);
    const parts = blockParts(block).map((part) => {
      if (part.type !== 'table') return part;
      count += 1;
      const named = first && title ? title : 'Tabela ' + count + ' da resposta';
      first = false;
      return Object.assign({}, part, { title: named });
    });
    return Object.assign({}, block, { parts: parts });
  });
};

const periodRange = (period) => {
  const source = objectOf(period);
  return parseIsoDate(source.from) && parseIsoDate(source.to) ? { from: source.from, to: source.to } : null;
};

export const periodText = (period) => {
  const source = objectOf(period);
  const label = cleanText(source.label, 120);
  if (parseIsoDate(source.from) && parseIsoDate(source.to)) {
    const range = formatDateFull(source.from) + ' a ' + formatDateFull(source.to);
    return 'Período: ' + range + (label && label.indexOf(formatDateFull(source.from)) === -1 ? ' (' + label + ')' : '');
  }
  return label ? 'Período: ' + label : '';
};

export const normalizeBody = (raw, cellLinks) => {
  const source = objectOf(raw);
  const links = listOf(cellLinks);
  const blocks = listOf(source.blocks).slice(0, ASSISTANT_LIMITS.blocks)
    .map((block, index) => normalizeBlock(block, index, links.filter((link) => link.block === index)))
    .filter(Boolean);
  return {
    headline: cleanText(source.headline, 1000),
    blocks: titledTables(blocks),
    period: periodRange(source.period),
    periodText: periodText(source.period),
    definitions: listOf(source.definitions)
      .map((item, index) => ({ key: 'd' + index, term: cleanText(objectOf(item).term, 120), text: cleanText(objectOf(item).text, 600) }))
      .filter((item) => item.term && item.text)
      .slice(0, ASSISTANT_LIMITS.items),
    caveats: stringList(source.caveats, ASSISTANT_LIMITS.items, 600),
    followUps: stringList(source.followUps, ASSISTANT_LIMITS.followUps, 300),
  };
};

const safeQuery = (raw, extra) => {
  const query = {};
  const source = objectOf(raw);
  const allowed = listOf(extra);
  Object.keys(source).forEach((key) => {
    if (!has(PI_FILTER_DEFAULTS, key) && allowed.indexOf(key) === -1) return;
    const value = source[key];
    if (typeof value !== 'string' && !isNumber(value) && typeof value !== 'boolean') return;
    const text = String(value).trim();
    if (!text || text.length > 120 || UNSAFE_QUERY.test(text)) return;
    query[key] = text;
  });
  return query;
};

const extraQueryKeys = (path) => {
  if (FEATURE_LINK_PATH.test(path)) return COMPANY_ADOPTION_QUERY_KEYS;
  if (AREA_LINK_PATH.test(path)) return AREA_QUERY_KEYS;
  return [];
};

const linkPath = (path) => {
  const metric = AREA_METRIC_LINK_PATH.exec(path);
  return metric ? metric[1] + metricPathSegment(metric[2]) : path;
};

export const safeLink = (raw, index) => {
  const source = objectOf(raw);
  const internal = safeInternalRoute(source.path);
  if (!internal || !LINK_PATH.test(internal)) return null;
  const path = linkPath(internal);
  const query = safeQuery(source.query, extraQueryKeys(path));
  return { key: 'l' + (isNumber(index) ? index : 0) + path, label: cleanText(source.label, 120) || 'Abrir no Product Intelligence', to: { path: path, query: query } };
};

export const normalizeLinks = (list) => {
  const seen = {};
  return listOf(list)
    .map(safeLink)
    .filter((link) => {
      if (!link) return false;
      const id = link.to.path + '?' + Object.keys(link.to.query).sort().map((key) => key + '=' + link.to.query[key]).join('&');
      if (seen[id]) return false;
      seen[id] = true;
      return true;
    })
    .slice(0, ASSISTANT_LIMITS.links);
};

const rowsText = (rows) => {
  if (!isNumber(rows)) return '';
  return rows === 1 ? '1 linha' : formatInteger(rows) + ' linhas';
};

export const normalizeTrace = (list) => listOf(list)
  .map((raw, index) => {
    const source = objectOf(raw);
    const tool = cleanText(source.tool, 60);
    const asOf = toInstant(source.asOf);
    return {
      key: 't' + index,
      step: intOr(source.step, index + 1),
      tool: tool,
      label: cleanText(source.label, 120) || tool || EMPTY,
      filters: stringList(source.filters, 12, 200),
      status: toolStatusInfo(source.status),
      rowsText: rowsText(source.rows),
      latencyText: latencyText(source.latencyMs),
      asOfText: asOf ? 'dados de ' + stampText(asOf) : '',
      partial: source.isPartial === true,
      notes: stringList(source.notes, 8, 400),
    };
  })
  .filter((step) => step.tool || step.label !== EMPTY);

export const warningItems = (list) => {
  const seen = {};
  return listOf(list)
    .map((raw) => {
      const code = cleanText(raw, 400);
      if (!code) return null;
      if (has(WARNING_TEXTS, code)) return { text: WARNING_TEXTS[code], number: false };
      return { text: code, number: NUMBER_WARNING.test(code) };
    })
    .filter((item) => {
      if (!item || seen[item.text]) return false;
      seen[item.text] = true;
      return true;
    })
    .map((item, index) => Object.assign({ key: 'w' + index }, item));
};

export const usageParts = (raw) => {
  const usage = objectOf(raw);
  const modelCalls = intOr(usage.modelCalls, 0);
  const toolCalls = intOr(usage.toolCalls, 0);
  const cached = intOr(usage.cachedInputTokens, 0);
  return {
    calls: formatInteger(modelCalls) + (modelCalls === 1 ? ' chamada ao modelo' : ' chamadas ao modelo') + ' · ' +
      formatInteger(toolCalls) + (toolCalls === 1 ? ' consulta ao PI' : ' consultas ao PI'),
    tokens: formatInteger(intOr(usage.inputTokens, 0)) + ' tokens de entrada' + (cached ? ' (' + formatInteger(cached) + ' em cache)' : '') +
      ' · ' + formatInteger(intOr(usage.outputTokens, 0)) + ' de saída',
    cost: costPair(numberOr(usage.costUsd, 0), numberOr(usage.costBrl, null)),
    time: latencyText(usage.latencyMs),
  };
};

export const routeText = (label, method) => {
  const text = has(ROUTE_TEXT, method) ? ROUTE_TEXT[method] : '';
  return text ? label + ' — ' + text : label;
};

export const answerEnvelope = (envelopes) => {
  const list = listOf(envelopes).filter((envelope) => envelope && typeof envelope === 'object' && !Array.isArray(envelope));
  if (!list.length) return null;
  let oldest = null;
  let asOf = null;
  let partial = false;
  let coverage = null;
  const versions = [];
  const notes = [];
  list.forEach((envelope) => {
    const instant = toInstant(envelope.asOf);
    if (instant && (oldest === null || instant.getTime() < oldest.getTime())) {
      oldest = instant;
      asOf = envelope.asOf;
    }
    if (envelope.isPartial === true) partial = true;
    if (isNumber(envelope.definitionVersion) && versions.indexOf(envelope.definitionVersion) === -1) versions.push(envelope.definitionVersion);
    listOf(envelope.notes).forEach((note) => {
      const text = cleanText(note, 600);
      if (text && notes.indexOf(text) === -1) notes.push(text);
    });
    const range = objectOf(envelope.coverage);
    if (!coverage && (range.collectionStartedAt || range.backfilledFrom)) coverage = range;
  });
  return { asOf: asOf, isPartial: partial, definitionVersion: versions.length === 1 ? versions[0] : null, coverage: coverage || {}, notes: notes };
};

export const normalizeAnswer = (raw) => {
  const source = objectOf(raw);
  const status = cleanText(source.status, 40);
  const agentUsed = cleanText(source.agentUsed, 40);
  const label = agentLabel(agentUsed, source.agentLabel);
  const interactionId = isNumber(source.interactionId) && source.interactionId > 0 ? Math.floor(source.interactionId) : 0;
  const envelopes = listOf(source.envelopes).filter((envelope) => envelope && typeof envelope === 'object' && !Array.isArray(envelope));
  return {
    interactionId: interactionId,
    conversationId: isGuid(source.conversationId) ? source.conversationId : '',
    turn: intOr(source.turn, 0),
    status: status,
    statusInfo: statusInfo(status),
    showStatus: status !== 'answered',
    agentRequested: cleanText(source.agentRequested, 40),
    agentUsed: agentUsed,
    agentLabel: label,
    routeText: routeText(label, source.routeMethod),
    body: normalizeBody(source.answer, normalizeCellLinks(source.cellLinks)),
    links: normalizeLinks(source.links),
    trace: normalizeTrace(source.trace),
    envelopes: envelopes,
    envelope: answerEnvelope(envelopes),
    warnings: warningItems(source.warnings),
    usage: usageParts(source.usage),
    askedAtText: stampText(source.askedAt),
  };
};

export const answerExportContext = (question, answer, screen) => {
  const source = objectOf(answer);
  const body = objectOf(source.body);
  const lines = [];
  const asked = cleanQuestion(question);
  if (asked) lines.push('Pergunta: "' + asked + '"');
  if (body.periodText) lines.push(body.periodText);
  if (source.routeText) lines.push('Agente: ' + source.routeText);
  return { screen: cleanText(screen, 80) || CHAT_SCREEN, filters: lines, period: body.period || null, envelope: source.envelope || null };
};

export const usageDetailRoute = (interactionId) => {
  if (!isNumber(interactionId) || interactionId <= 0) return null;
  return { path: ASSISTANT_USAGE_PATH, query: { detalhe: String(Math.floor(interactionId)) } };
};

export const subviewLinks = (query) => {
  const source = objectOf(query);
  const kept = {};
  Object.keys(PI_FILTER_DEFAULTS).forEach((key) => {
    if (typeof source[key] === 'string' && source[key]) kept[key] = source[key];
  });
  return ASSISTANT_SUBVIEWS.map((view) => ({ key: view.key, label: view.label, to: { path: view.path, query: kept } }));
};

const errorBody = (error) => objectOf(error && error.response && error.response.data);

const retryAfterOf = (error, body) => {
  if (isNumber(body.retryAfterSeconds) && body.retryAfterSeconds > 0) return Math.ceil(body.retryAfterSeconds);
  const headers = objectOf(error && error.response && error.response.headers);
  const header = Number(headers['retry-after'] || headers['Retry-After']);
  return isFinite(header) && header > 0 ? Math.ceil(header) : null;
};

export const retryText = (seconds) => {
  if (!isNumber(seconds) || seconds <= 0) return '';
  if (seconds < 60) return 'em ' + Math.ceil(seconds) + ' s';
  if (seconds < 3600) return 'em ' + Math.ceil(seconds / 60) + ' min';
  return 'em ' + Math.ceil(seconds / 3600) + ' h';
};

const pad2 = (value) => (value < 10 ? '0' : '') + value;

export const countdownText = (seconds) => {
  if (!isNumber(seconds) || seconds <= 0) return '';
  const total = Math.ceil(seconds);
  if (total >= 3600) return Math.floor(total / 3600) + ' h ' + pad2(Math.floor((total % 3600) / 60)) + ' min';
  return Math.floor(total / 60) + ':' + pad2(total % 60);
};

const limitText = (limits) => {
  const source = objectOf(limits);
  const caps = [];
  if (isNumber(source.perUser10Min)) caps.push(source.perUser10Min + ' a cada 10 min');
  if (isNumber(source.perUserDay)) caps.push(source.perUserDay + ' por dia');
  return caps.length ? ' (até ' + joinPt(caps) + ' por pessoa)' : '';
};

const errorResult = (kind, status, code, message, extra) => Object.assign({
  kind: kind,
  status: status,
  code: code,
  message: message,
  retryAfterSeconds: null,
  retryable: false,
  blocking: false,
}, extra || {});

export const assistantError = (error, limits, action) => {
  const info = classifyError(error);
  const body = errorBody(error);
  const code = typeof body.code === 'string' ? body.code : '';
  const server = typeof body.message === 'string' && body.message.length <= 300 ? cleanText(body.message, 300) : '';
  const status = info.status;
  if ((error && error.code === NO_CROSS_TOKEN_CODE) || info.kind === 'session') {
    return errorResult('session', null, code, 'Saia e entre novamente no Master para autenticar na API v2.', { blocking: true });
  }
  if (status === 503 || code === 'assistant_not_configured') return errorResult('not_configured', status, code, NOT_CONFIGURED_TEXT);
  if (status === 409 || code === 'assistant_busy') return errorResult('busy', status, code, 'Aguarde a pergunta anterior terminar.');
  if (status === 429) {
    const retry = retryAfterOf(error, body);
    const when = retryText(retry);
    if (code === 'assistant_daily_budget') {
      return errorResult('daily_budget', status, code, 'Teto diário de custo do assistente atingido para todos; volta amanhã' + (when ? ' (' + when + ')' : '') + '.',
        { retryAfterSeconds: retry });
    }
    return errorResult('rate_limited', status, code, 'Você atingiu o limite de perguntas' + limitText(limits) + '. Tente de novo ' + (when || 'mais tarde') + '.',
      { retryAfterSeconds: retry });
  }
  if (status === 403 && code === 'assistant_revenue_denied') {
    return errorResult('revenue_denied', status, code, 'Sem acesso a Receita e oportunidades: escolha outro agente.');
  }
  if (info.kind === 'forbidden') {
    return errorResult('forbidden', status, code, 'Sem permissão (' + status + '). É necessário um usuário operador da plataforma.', { blocking: true });
  }
  if (info.kind === 'disabled') return errorResult('disabled', status, code, DISABLED_TEXT, { blocking: true });
  if (info.kind === 'invalid') return errorResult('invalid', status, code, server || 'Pergunta inválida (400).');
  return errorResult('failed', status, code, 'Não foi possível ' + (action || 'enviar a pergunta') + (status ? ' (' + status + ')' : '') + '. Tente de novo.',
    { retryable: true });
};

export const agentsLoadError = (error) => {
  const result = assistantError(error, null, 'carregar o assistente');
  const kind = result.kind === 'not_configured' || result.kind === 'busy' || result.kind === 'rate_limited' || result.kind === 'daily_budget' ||
    result.kind === 'revenue_denied' ? 'failed' : result.kind;
  return { kind: kind, status: result.status, message: result.message, retryable: kind === 'failed' };
};

export const feedbackError = (error) => {
  const info = classifyError(error);
  if (info.status === 404) return 'Só quem fez a pergunta pode avaliá-la (404).';
  if (info.status === 400) return 'Avaliação inválida: o comentário vai até ' + ASSISTANT_LIMITS.comment + ' caracteres (400).';
  if (info.kind === 'forbidden') return 'Sem permissão para avaliar (' + info.status + ').';
  return 'Não foi possível registrar a avaliação' + (info.status ? ' (' + info.status + ')' : '') + '.';
};

export const historyKey = (owner) => HISTORY_PREFIX + (typeof owner === 'string' ? owner : '');

export const assistantOwner = (storage) => {
  if (!storage) return '';
  try {
    const info = JSON.parse(storage.getItem(MASTER_USER_KEY) || 'null');
    return info && info.id ? String(info.id) : '';
  } catch (e) {
    return '';
  }
};

export const emptyHistory = () => ({ conversationId: '', turns: [] });

const storedFeedback = (raw) => {
  const source = objectOf(raw);
  if (!has(FEEDBACK_INFO, source.value)) return null;
  return { value: source.value, comment: cleanText(source.comment, ASSISTANT_LIMITS.comment) };
};

const storedTurn = (raw) => {
  const source = objectOf(raw);
  const question = cleanQuestion(source.question);
  const answer = objectOf(source.answer);
  if (!question || !Object.keys(answer).length) return null;
  return { question: question, answer: answer, feedback: storedFeedback(source.feedback) };
};

export const readHistory = (storage, owner) => {
  if (!storage) return emptyHistory();
  try {
    const saved = objectOf(JSON.parse(storage.getItem(historyKey(owner)) || 'null'));
    const turns = listOf(saved.turns).map(storedTurn).filter(Boolean).slice(-ASSISTANT_LIMITS.turnsShown);
    return { conversationId: isGuid(saved.conversationId) && turns.length ? saved.conversationId : '', turns: turns };
  } catch (e) {
    return emptyHistory();
  }
};

const savedTurnId = (turn) => {
  const id = objectOf(objectOf(turn).answer).interactionId;
  return isNumber(id) && id > 0 ? id : 0;
};

export const appendTurn = (history, question, answer) => {
  const source = objectOf(history);
  const raw = objectOf(answer);
  const turn = { question: cleanQuestion(question), answer: raw, feedback: null };
  const id = savedTurnId(turn);
  const turns = listOf(source.turns);
  const known = id > 0 && turns.some((item) => savedTurnId(item) === id);
  return {
    conversationId: isGuid(raw.conversationId) ? raw.conversationId : (isGuid(source.conversationId) ? source.conversationId : ''),
    turns: (known ? turns : turns.concat([turn])).slice(-ASSISTANT_LIMITS.turnsShown),
  };
};

export const setTurnFeedback = (history, interactionId, feedback) => {
  const source = objectOf(history);
  const value = storedFeedback(feedback);
  return {
    conversationId: source.conversationId || '',
    turns: listOf(source.turns).map((turn) => (objectOf(turn).answer && turn.answer.interactionId === interactionId
      ? Object.assign({}, turn, { feedback: value })
      : turn)),
  };
};

export const feedbackFromResult = (result, fallback) => {
  const source = objectOf(result);
  const value = has(FEEDBACK_INFO, source.feedback) ? source.feedback : objectOf(fallback).value;
  if (!has(FEEDBACK_INFO, value)) return null;
  const comment = typeof source.comment === 'string' ? source.comment : objectOf(fallback).comment;
  return { value: value, comment: cleanText(comment, ASSISTANT_LIMITS.comment) };
};

export const summaryText = (value, max) => {
  const text = cleanText(value, 4000).replace(/\s+/g, ' ');
  const limit = isNumber(max) && max > 1 ? max : CHAT_BACK_SUMMARY;
  return text.length > limit ? text.substring(0, limit - 1) + '…' : text;
};

const turnNumberOf = (turn) => {
  const value = objectOf(objectOf(turn).answer).turn;
  return isNumber(value) && value > 0 && value <= 99999 ? Math.floor(value) : 0;
};

export const chatTurnNumber = (turn, index) => turnNumberOf(turn) || (isNumber(index) && index >= 0 ? index + 1 : 0);

export const chatTurnAnchor = (turn, index) => {
  const id = savedTurnId(turn);
  return id > 0 ? 'i' + id : 'p' + (isNumber(index) && index >= 0 ? index + 1 : 0);
};

export const emptyChatState = (owner) => ({
  owner: typeof owner === 'string' ? owner : '',
  conversationId: '',
  turns: [],
  agent: '',
  draft: '',
  scrollY: 0,
  visited: [],
  back: null,
});

const linkIdOf = (value) => (typeof value === 'string' && LINK_ID.test(value) ? value : '');

const storedBack = (raw) => {
  const source = objectOf(raw);
  const turn = isNumber(source.turn) && source.turn > 0 && source.turn <= 99999 ? Math.floor(source.turn) : 0;
  const anchor = typeof source.anchor === 'string' && TURN_ANCHOR.test(source.anchor) ? source.anchor : '';
  if (!turn || !anchor) return null;
  return {
    conversationId: isGuid(source.conversationId) ? source.conversationId : '',
    turn: turn,
    anchor: anchor,
    link: linkIdOf(source.link),
    question: summaryText(source.question, CHAT_BACK_SUMMARY),
    hidden: source.hidden === true,
  };
};

export const sanitizeChatState = (raw, owner) => {
  const source = objectOf(raw);
  const turns = listOf(source.turns).map(storedTurn).filter(Boolean).slice(-ASSISTANT_LIMITS.turnsShown);
  return {
    owner: emptyChatState(owner).owner,
    conversationId: isGuid(source.conversationId) && turns.length ? source.conversationId : '',
    turns: turns,
    agent: typeof source.agent === 'string' && AGENT_KEY.test(source.agent) ? source.agent : '',
    draft: typeof source.draft === 'string' ? source.draft.substring(0, ASSISTANT_LIMITS.question) : '',
    scrollY: isNumber(source.scrollY) && source.scrollY > 0 ? Math.round(source.scrollY) : 0,
    visited: listOf(source.visited).filter((key) => !!linkIdOf(key)).slice(-CHAT_VISITED_MAX),
    back: storedBack(source.back),
  };
};

export const readChatState = (storage, owner) => {
  if (!storage) return emptyChatState(owner);
  try {
    const raw = storage.getItem(CHAT_STORAGE_KEY);
    if (!raw) return Object.assign(emptyChatState(owner), readHistory(storage, owner));
    const saved = objectOf(JSON.parse(raw));
    if ((typeof saved.owner === 'string' ? saved.owner : '') !== emptyChatState(owner).owner) return emptyChatState(owner);
    return sanitizeChatState(saved, owner);
  } catch (e) {
    return emptyChatState(owner);
  }
};

export const writeChatState = (storage, state) => {
  if (!storage) return;
  const clean = sanitizeChatState(state, objectOf(state).owner);
  try {
    storage.setItem(CHAT_STORAGE_KEY, JSON.stringify(clean));
    storage.removeItem(historyKey(clean.owner));
  } catch (e) {
    return;
  }
};

export const restartChat = (state) => {
  const source = objectOf(state);
  return Object.assign(emptyChatState(source.owner), {
    agent: typeof source.agent === 'string' ? source.agent : '',
    draft: typeof source.draft === 'string' ? source.draft : '',
  });
};

export const chatLinkQuery = (conversationId, turn) => {
  const query = {};
  if (!isGuid(conversationId) || !isNumber(turn) || turn <= 0) return query;
  query[CHAT_QUERY.conversation] = conversationId;
  query[CHAT_QUERY.turn] = String(Math.floor(turn));
  return query;
};

export const withChatQuery = (to, chatQuery) => {
  const target = objectOf(to);
  return { path: target.path, query: Object.assign({}, objectOf(target.query), objectOf(chatQuery)) };
};

export const chatOrigin = (query) => {
  const source = objectOf(query);
  const turn = queryText(source, CHAT_QUERY.turn);
  if (!/^\d{1,5}$/.test(turn) || Number(turn) <= 0) return null;
  const conversationId = queryText(source, CHAT_QUERY.conversation);
  return { conversationId: isGuid(conversationId) ? conversationId : '', turn: Number(turn) };
};

export const withoutChatQuery = (query) => {
  const source = objectOf(query);
  const next = {};
  Object.keys(source).forEach((key) => {
    if (key !== CHAT_QUERY.conversation && key !== CHAT_QUERY.turn) next[key] = source[key];
  });
  return next;
};

export const chatTurnIndex = (turns, target) => {
  const list = listOf(turns);
  const source = objectOf(target);
  if (source.anchor) {
    for (let i = list.length - 1; i >= 0; i--) {
      if (chatTurnAnchor(list[i], i) === source.anchor) return i;
    }
  }
  if (isNumber(source.turn) && source.turn > 0) {
    for (let i = list.length - 1; i >= 0; i--) {
      if (turnNumberOf(list[i]) === source.turn) return i;
    }
    if (source.turn <= list.length && !turnNumberOf(list[source.turn - 1])) return source.turn - 1;
  }
  return -1;
};

export const chatBack = (state, index, link) => {
  const source = objectOf(state);
  const turn = listOf(source.turns)[index];
  if (!turn) return null;
  return {
    conversationId: isGuid(source.conversationId) ? source.conversationId : '',
    turn: chatTurnNumber(turn, index),
    anchor: chatTurnAnchor(turn, index),
    link: linkIdOf(link),
    question: summaryText(turn.question, CHAT_BACK_SUMMARY),
    hidden: false,
  };
};

export const backFromQuery = (state, query) => {
  const source = objectOf(state);
  const origin = chatOrigin(query);
  if (!origin || !origin.conversationId || origin.conversationId !== source.conversationId) return null;
  const index = chatTurnIndex(source.turns, { turn: origin.turn });
  return index === -1 ? null : chatBack(source, index, '');
};

export const chatReturnRoute = (back) => {
  const source = objectOf(back);
  const query = {};
  if (isGuid(source.conversationId)) query[CHAT_QUERY.conversation] = source.conversationId;
  if (isNumber(source.turn) && source.turn > 0) query[CHAT_QUERY.turn] = String(source.turn);
  return { path: ASSISTANT_PATH, query: query };
};

export const chatTarget = (state, query) => {
  const source = objectOf(state);
  const origin = chatOrigin(query);
  if (!origin || (origin.conversationId && origin.conversationId !== source.conversationId)) return null;
  const back = source.back ? objectOf(source.back) : null;
  const same = !!back && back.turn === origin.turn && (!origin.conversationId || back.conversationId === origin.conversationId);
  const index = chatTurnIndex(source.turns, same ? { anchor: back.anchor, turn: origin.turn } : { turn: origin.turn });
  if (index === -1) return null;
  return { index: index, anchor: chatTurnAnchor(source.turns[index], index), link: same ? back.link : '' };
};

export const isChatReturn = (query, back) => !!(chatOrigin(query) || storedBack(back));

const pathOf = (path) => (typeof path === 'string' ? path.replace(/\/+$/, '').toLowerCase() : '');

export const isChatPath = (path) => pathOf(path) === ASSISTANT_PATH;

export const chatBackVisible = (back, path) => {
  const value = pathOf(path);
  return !!back && objectOf(back).hidden !== true && isPiPath(value) && !isChatPath(value) && value !== UNLOCK_PATH;
};

export const visitedKey = (anchor, link) => anchor + '|' + link;

export const withVisited = (state, key) => {
  const source = objectOf(state);
  const list = listOf(source.visited).filter((item) => item !== key);
  if (linkIdOf(key)) list.push(key);
  return Object.assign({}, source, { visited: list.slice(-CHAT_VISITED_MAX) });
};

export const visitedLinks = (visited, anchor) => {
  const prefix = anchor + '|';
  return listOf(visited)
    .filter((key) => typeof key === 'string' && key.indexOf(prefix) === 0)
    .map((key) => key.substring(prefix.length));
};



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piAssistantModel.js